import { isFamilyAsset, ASSET_FAMILY } from '@exodus/assets';
import { atom, swr } from '@exodus/headless-ui';
import { getAssetPendingNonExchangeTxs } from '@exodus/ethereum-lib';
import ms from 'ms';
import fromAssetAtom from './from-asset.js';
import fromWalletAccountAtom from './from-wallet-account.js';
import fromAssetReadyAtom from './from-asset-ready.js';
import isSendAllAtom from './is-send-all.js';
import toAssetAtom from './to-asset.js';
import toAmountAtom from './to-amount.js';
import toAssetReadyAtom from './to-asset-ready.js';
import toWalletAccountAtom from './to-wallet-account.js';
import availabilityAtom from './availability.js';
import fromAmountAtom from './from-amount.js';
import feeAmountAtom from './fee-amount.js';
import feeSpendableBalanceAtom from './fee-spendable-balance.js';
import limitsAtom from './limits.js';
import { fromSpendableBalanceAtom, fromTotalBalanceAtom, prefillIdAtom, quoteAtom, swapModeAtom, toFiatAmountAtom, } from './index.js';
const fromWalletAccountIsReadOnlyAtom = atom((get, { selectors }) => {
    const fromAsset = get(fromAssetAtom);
    const fromWalletAccount = get(fromWalletAccountAtom);
    const getIsReadOnlyWalletAccount = get(selectors.walletAccounts.isReadOnly);
    const fromWalletAccountIsReadOnly = getIsReadOnlyWalletAccount(fromWalletAccount.toString());
    if (!fromWalletAccountIsReadOnly)
        return null;
    return {
        type: 'warning',
        reason: 'READ_ONLY_WALLET_ACCOUNT',
        payload: { walletAccount: fromWalletAccount, asset: fromAsset },
    };
});
const fromAssetNotReadyAtom = atom((get) => {
    const fromAssetReady = get(swr(fromAssetReadyAtom));
    if (fromAssetReady.data || fromAssetReady.loading || fromAssetReady.validating)
        return null;
    return {
        type: 'warning',
        reason: 'FROM_ASSET_NOT_READY',
        payload: {
            fromAsset: get(fromAssetAtom),
            toAsset: get(toAssetAtom),
            fromWalletAccount: get(fromWalletAccountAtom).toString(),
        },
    };
});
const toAssetNotReadyAtom = atom((get) => {
    const toAssetReady = get(swr(toAssetReadyAtom));
    if (toAssetReady.data || toAssetReady.loading || toAssetReady.validating)
        return null;
    return {
        type: 'warning',
        reason: 'TO_ASSET_NOT_READY',
        payload: {
            toAsset: get(toAssetAtom),
            toWalletAccount: get(toWalletAccountAtom).toString(),
            toAmount: get(toAmountAtom),
        },
    };
});
const exchangeUnavailableAtom = atom((get) => {
    const exchangeAvailability = get(availabilityAtom);
    if (exchangeAvailability.isAvailable)
        return null;
    return {
        type: 'error',
        reason: 'EXCHANGE_UNAVAILABLE',
        payload: exchangeAvailability.reason,
    };
});
const mustOptInTokenAtom = atom((get, { selectors }) => {
    const toAsset = get(toAssetAtom);
    const toWalletAccount = get(toWalletAccountAtom);
    const getMustOptInToken = get(selectors.assets.mustOptInToken);
    if (toAsset.assetType !== 'ALGORAND_TOKEN' && toAsset.assetType !== 'RIPPLE_TOKEN')
        return null;
    if (!getMustOptInToken(toAsset.name, toWalletAccount.toString()))
        return null;
    return {
        type: 'error',
        reason: 'MUST_OPT_IN',
        payload: { toAsset, toWalletAccount: toWalletAccount.toString() },
    };
});
const rentExemptSolanaBalanceAtom = atom((get, { selectors }) => {
    const fromAsset = get(fromAssetAtom);
    const fromAmount = get(fromAmountAtom);
    if (fromAsset.feeAsset.name !== 'solana' || !fromAmount.isPositive)
        return null;
    const isSolSend = fromAsset.name === 'solana';
    if (isSolSend && get(isSendAllAtom))
        return null;
    const quote = get(swr(quoteAtom));
    if (quote.validating && !quote.data)
        return null;
    const fee = get(feeAmountAtom);
    const feeSpendableBalance = get(feeSpendableBalanceAtom);
    const walletAccount = get(fromWalletAccountAtom).toString();
    const assetSource = { walletAccount, assetName: 'solana' };
    const accountState = get(selectors.accountStates.createAssetSourceSelector(assetSource));
    const rentExemptAmount = accountState?.rentExemptAmount;
    if (!rentExemptAmount)
        return null;
    const remaining = isSolSend
        ? feeSpendableBalance.sub(fee).sub(fromAmount)
        : feeSpendableBalance.sub(fee);
    if (remaining.isZero)
        return null;
    if (remaining.isPositive && remaining.lt(rentExemptAmount)) {
        return {
            type: 'error',
            reason: 'LOW_SOL_RENT_EXEMPT_AMOUNT',
            payload: { fromAsset, rentExemptAmount },
        };
    }
    return null;
});
const zeroBalanceAtom = atom((get) => {
    const asset = get(fromAssetAtom);
    const fromAmount = get(fromAmountAtom);
    const balance = get(fromSpendableBalanceAtom);
    const prefillId = get(prefillIdAtom);
    if (!balance.isZero)
        return null;
    if (fromAmount.isZero && !prefillId)
        return null;
    return {
        type: 'error',
        reason: 'ZERO_BALANCE',
        payload: { asset, balance },
    };
});
const notEnoughBalanceAtom = atom((get) => {
    const prefillId = get(prefillIdAtom);
    if (prefillId && get(fromAmountAtom).isZero)
        return null;
    const limits = get(swr(limitsAtom));
    const fee = get(feeAmountAtom);
    const asset = get(fromAssetAtom);
    const amount = get(fromAmountAtom);
    const balance = get(fromSpendableBalanceAtom);
    const totalBalance = get(fromTotalBalanceAtom);
    const feeBalance = get(feeSpendableBalanceAtom);
    const isAmountExceedingBalance = amount.gt(balance);
    const isAmountExceedingTotal = amount.gt(totalBalance);
    const isFeeAsset = asset.name === asset.feeAsset.name;
    const isFeeTooHighForFeeAsset = isFeeAsset && feeBalance && feeBalance.lt(amount.add(fee));
    if (!isFeeTooHighForFeeAsset && !isAmountExceedingBalance && !isAmountExceedingTotal) {
        return null;
    }
    return {
        type: 'error',
        reason: 'NOT_ENOUGH_BALANCE',
        payload: {
            asset,
            prefillId,
            min: limits.data?.ExactIn.min,
        },
    };
});
const lowFromAmountAtom = atom((get) => {
    const limits = get(swr(limitsAtom));
    if (!limits.data)
        return null;
    const asset = get(fromAssetAtom);
    const swapLimits = limits.data.ExactIn;
    const amount = get(fromAmountAtom);
    if (!amount.isPositive || amount.gte(swapLimits.min)) {
        return null;
    }
    return {
        type: 'error',
        reason: 'LOW_FROM_AMOUNT',
        payload: { asset, min: swapLimits.min },
    };
});
const highFromAmountAtom = atom((get) => {
    const limits = get(swr(limitsAtom));
    if (!limits.data)
        return null;
    const swapMode = get(swapModeAtom);
    const isExactIn = swapMode === 'ExactIn';
    const swapLimits = isExactIn ? limits.data.ExactIn : limits.data.ExactOut;
    const swapAmount = isExactIn ? get(fromAmountAtom) : get(toAmountAtom);
    if (!swapAmount.isPositive || swapAmount.lte(swapLimits.max))
        return null;
    const swapAsset = isExactIn ? get(fromAssetAtom) : get(toAssetAtom);
    return {
        type: 'error',
        reason: 'HIGH_FROM_AMOUNT',
        payload: { limits: swapLimits, asset: swapAsset },
    };
});
const notEnoughFeeBalanceAtom = atom((get) => {
    const fromAsset = get(fromAssetAtom);
    const isFromFeeAsset = fromAsset.name === fromAsset.feeAsset.name;
    if (isFromFeeAsset)
        return null;
    const fee = get(feeAmountAtom);
    const fromAmount = get(fromAmountAtom);
    const feeSpendableBalance = get(feeSpendableBalanceAtom);
    if (!fromAmount.isPositive || feeSpendableBalance.gte(fee))
        return null;
    return {
        type: 'error',
        reason: 'NOT_ENOUGH_FEE_BALANCE',
        payload: { fee, fromAsset },
    };
});
const exactOutQuoteErrorAtom = atom((get) => {
    const swapMode = get(swapModeAtom);
    const isExactOut = swapMode === 'ExactOut';
    if (!isExactOut)
        return null;
    const toAmount = get(toAmountAtom);
    if (!toAmount.isZero)
        return null;
    const quote = get(swr(quoteAtom));
    if (quote.validating && !quote.data)
        return null;
    if (!quote.data || quote.error) {
        return {
            type: 'error',
            reason: 'EXACT_OUT_QUOTE',
            payload: {
                fromAsset: get(fromAssetAtom),
                toAsset: get(toAssetAtom),
                toAmount,
                toFiatAmount: get(toFiatAmountAtom),
                error: quote.error,
            },
        };
    }
    return null;
});
const pairUnavailableAtom = atom((get) => {
    const quote = get(swr(quoteAtom));
    const limits = get(swr(limitsAtom));
    const swapMode = get(swapModeAtom);
    const fromAmount = get(fromAmountAtom);
    const toAmount = get(toAmountAtom);
    const hasInputAmount = swapMode === 'ExactOut' ? !toAmount.isZero : !fromAmount.isZero;
    const quoteError = hasInputAmount && !quote.validating && !quote.data;
    const limitsError = !limits.validating && !limits.data;
    if (!quoteError && !limitsError)
        return null;
    const cause = limitsError ? 'limits' : 'quote';
    return {
        type: 'error',
        reason: 'PAIR_UNAVAILABLE',
        payload: {
            fromAsset: get(fromAssetAtom),
            toAsset: get(toAssetAtom),
            cause,
        },
    };
});
const eosAccountCreationInProgressAtom = atom((get, { selectors }) => {
    const toAsset = get(toAssetAtom);
    const assetName = toAsset.name;
    const walletAccount = get(toWalletAccountAtom).toString();
    const assetSource = { walletAccount, assetName };
    if (toAsset.name !== 'eosio')
        return null;
    const accountState = get(selectors.accountStates.createAssetSourceSelector(assetSource));
    if (accountState?.mainAccountName)
        return null;
    const incomingOrders = get(selectors.orders.assetIncomingOrders)(assetSource);
    if (incomingOrders.size === 0)
        return null;
    return {
        type: 'error',
        reason: 'EOS_ACCOUNT_CREATION_IN_PROGRESS',
        payload: {},
    };
});
const transactionStuckAtom = atom((get, { selectors }) => {
    const fromAsset = get(fromAssetAtom);
    if (!isFamilyAsset({ asset: fromAsset.baseAsset, family: ASSET_FAMILY.EVM }))
        return null;
    const assetName = fromAsset.baseAsset.name;
    const walletAccount = get(fromWalletAccountAtom).toString();
    const assetSource = { assetName, walletAccount };
    const getIsExchangeTx = get(selectors.orders.getIsExchangeTx);
    const txLog = get(selectors.txLog.createAssetSourceSelector(assetSource));
    const pendingTxs = getAssetPendingNonExchangeTxs(assetName, walletAccount, () => txLog, getIsExchangeTx);
    const now = Date.now();
    if (pendingTxs.some((tx) => now - tx.date.getTime() > ms('5m'))) {
        return {
            type: 'error',
            reason: 'TRANSACTION_STUCK',
            payload: { fromAsset },
        };
    }
    return null;
});
const blockingValidationAtoms = [
    fromWalletAccountIsReadOnlyAtom,
    fromAssetNotReadyAtom,
    toAssetNotReadyAtom,
    exchangeUnavailableAtom,
    mustOptInTokenAtom,
    rentExemptSolanaBalanceAtom,
    zeroBalanceAtom,
    highFromAmountAtom,
    notEnoughBalanceAtom,
    lowFromAmountAtom,
    notEnoughFeeBalanceAtom,
    exactOutQuoteErrorAtom,
    pairUnavailableAtom,
    eosAccountCreationInProgressAtom,
    transactionStuckAtom,
];
const isAmountIndependent = (error) => {
    switch (error.reason) {
        case 'READ_ONLY_WALLET_ACCOUNT':
        case 'FROM_ASSET_NOT_READY':
        case 'TO_ASSET_NOT_READY':
        case 'EXCHANGE_UNAVAILABLE':
        case 'MUST_OPT_IN':
        case 'EOS_ACCOUNT_CREATION_IN_PROGRESS':
        case 'TRANSACTION_STUCK':
            return true;
        case 'PAIR_UNAVAILABLE':
            return error.payload.cause === 'limits';
        default:
            return false;
    }
};
const blockingValidationsAtom = atom((get) => {
    const validations = blockingValidationAtoms.map((atom) => get(atom));
    return validations.filter((v) => v !== null);
});
export const errorAtom = atom((get) => {
    const all = get(blockingValidationsAtom);
    return all.find(isAmountIndependent) ?? all[0] ?? null;
});
export const visibleErrorAtom = atom((get) => {
    const error = get(errorAtom);
    if (!error)
        return null;
    const swapMode = get(swapModeAtom);
    const swapAmount = swapMode === 'ExactIn' ? get(fromAmountAtom) : get(toAmountAtom);
    const didUserInteract = swapAmount.isPositive || !!get(prefillIdAtom);
    if (!didUserInteract && !isAmountIndependent(error))
        return null;
    return error;
});
export const preventFromExchangingAtom = atom((get) => {
    const limits = get(swr(limitsAtom));
    if (limits.validating && !limits.data)
        return true;
    return get(blockingValidationsAtom).length > 0;
});
